import {useState, useEffect} from 'react';
import {graphql, forecastFetchQuery} from '../../../graphql/forecastRelay';
import {useDebounce} from './useDebounce';
import type {useTicketSearchQuery$data} from './__generated__/useTicketSearchQuery.graphql';

const TicketSearchQueryDoc = graphql`
	query useTicketSearchQuery($search: String!) {
		viewer {
			tickets(first: 10, filters: {search: $search}) {
				edges {
					node {
						id
						number
						subject
						status
					}
				}
			}
		}
	}
`;

export interface TicketSearchResult {
	id: string;
	number: number;
	subject: string;
	status: string;
}

export function useTicketSearch(query: string) {
	const debouncedQuery = useDebounce(query, 300);
	const [results, setResults] = useState<TicketSearchResult[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!debouncedQuery || debouncedQuery.length < 1) {
			setResults([]);
			setLoading(false);
			return;
		}

		let cancelled = false;
		setLoading(true);

		forecastFetchQuery(TicketSearchQueryDoc, {search: debouncedQuery})
			.then(data => {
				if (cancelled) return;
				const typed = data as useTicketSearchQuery$data | null;
				setResults(
					(typed?.viewer?.tickets.edges ?? []).flatMap(edge =>
						edge?.node
							? [
									{
										id: edge.node.id,
										number: edge.node.number,
										subject: edge.node.subject,
										status: edge.node.status,
									},
							  ]
							: []
					)
				);
				setLoading(false);
			})
			.catch(() => {
				if (cancelled) return;
				setResults([]);
				setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [debouncedQuery]);

	return {results, loading};
}
